"use client";

import {
  Shield, Smartphone, QrCode, Key, Download, CheckCircle, XCircle, AlertCircle,
} from "lucide-react";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";

import { cn } from "@/app/_module/lib/utils";
import { Button } from "@/app/_module/components/ui/button";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import { Card, CardContent } from "@/app/_module/components/ui/card";
import { Input } from "@/app/_module/components/ui/input";
import { Label } from "@/app/_module/components/ui/label";
import { Badge } from "@/app/_module/components/ui/badge";
import { useToast } from "@/app/_module/hooks/use-toast";
import api from "@/app/_module/services/api";
import { useBranding } from "@/app/_module/store/contexts/brandingContext";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import {
  Dialog,
  ResponsiveDialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/app/_module/components/ui/dialog";
import { Alert, AlertDescription } from "@/app/_module/components/ui/alert";
import { isDemoMode } from "@/app/_module/utils/demoMode";

interface MFASettings {
  smsEnabled: boolean;
  totpEnabled: boolean;
  preferredMethod: "sms" | "totp";
  backupCodes?: string[];
  /** Direct 130 agents: the authenticator app alone, no SMS. */
  totpOnly?: boolean;
}

interface TOTPSetupData {
  manualEntryKey: string;
  qrCodeUrl: string;
  backupCodes: string[];
}

// ── Animation variants ────────────────────────────────────────
const EASE = [0.22, 1, 0.36, 1] as [number, number, number, number];

const stagger = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: EASE },
  },
};

export const MFAManagement = function () {
  const { branding } = useBranding();
  const { toast } = useToast();
  const { refetchUser } = useUserContext();
  const [mfaSettings, setMfaSettings] = useState<MFASettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showTOTPSetup, setShowTOTPSetup] = useState(false);
  const [totpSetupData, setTotpSetupData] = useState<TOTPSetupData | null>(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [backupCodesDownloaded, setBackupCodesDownloaded] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);

  const [showSMSSetup, setShowSMSSetup] = useState(false);
  const [smsPhoneNumber, setSmsPhoneNumber] = useState("");
  const [smsVerificationCode, setSmsVerificationCode] = useState("");
  const [isSmsSending, setIsSmsSending] = useState(false);
  const [isReconfiguring, setIsReconfiguring] = useState(false);

  useEffect(() => {
    fetchMFASettings();
  }, []);

  const getUserId = async (): Promise<string> => {
    const userProfileResponse = await api.get({
      url: getApiUrl(endpoint.user.getUserProfile),
      auth: true,
    });
    const userId =
      (userProfileResponse.data as any)?.user?.id ||
      (userProfileResponse.data as any)?.enrichedUser?.userId;
    if (!userId) throw new Error("User ID not found");
    return userId;
  };

  const fetchMFASettings = async () => {
    try {
      setIsLoading(true);
      const userId = await getUserId();
      const response = await api.get({
        url: `${getApiUrl(endpoint.auth.getMFAStatus)}?userId=${userId}`,
        auth: true,
      });
      const mfaData = (response as any)?.data;
      if (mfaData) {
        setMfaSettings(mfaData);
        refetchUser((prev) => prev);
        if (mfaData.totpEnabled && (mfaData.smsEnabled || mfaData.totpOnly)) {
          await resetMfaPromptCount();
        }
      }
    } catch {
      toast({ title: "Error", description: "Failed to load MFA settings.", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const resetMfaPromptCount = async () => {
    try {
      await api.post({ url: getApiUrl(endpoint.auth.mfaPromptReset), auth: true });
    } catch {
      // Silently fail
    }
  };

  const setupTOTP = async (reconfigure = false) => {
    // Same reasoning as startSMSSetup below — the backend already refuses this, but the
    // visitor should see the offer on click rather than after a failed request.
    if (isDemoMode()) {
      window.dispatchEvent(
        new CustomEvent('demo-restricted', {
          detail:
            "Setting up two-factor authentication is disabled in the demo sandbox. Start a free trial to secure your real account.",
        }),
      );
      return;
    }

    try {
      if (reconfigure) setIsReconfiguring(true);
      const userId = await getUserId();
      const response = await api.post({
        url: getApiUrl(endpoint.auth.setupMFA),
        body: { userId, method: "totp" },
        auth: true,
      });
      const data = (response.data as any)?.data || response.data;
      if (!data || !data.qrCodeUrl) throw new Error("Invalid response format");
      setTotpSetupData(data);
      setShowTOTPSetup(true);
      setIsReconfiguring(reconfigure);
    } catch {
      toast({ title: "Setup Failed", description: "Failed to setup authenticator app.", variant: "destructive" });
    }
  };

  const verifyTOTP = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast({ title: "Invalid Code", description: "Please enter a 6-digit code.", variant: "destructive" });
      return;
    }
    try {
      setIsVerifying(true);
      const userId = await getUserId();
      await api.post({
        url: getApiUrl(endpoint.auth.verifyMFA),
        body: { userId, code: verificationCode },
        auth: true,
      });
      toast({
        title: isReconfiguring ? "Authenticator Reconfigured" : "TOTP Enabled",
        description: isReconfiguring
          ? "Your authenticator app has been reconfigured successfully. Please save your new backup codes!"
          : "Authenticator app has been successfully enabled. Please save your backup codes!",
      });
      setShowBackupCodes(true);
      setVerificationCode("");
    } catch {
      toast({ title: "Verification Failed", description: "Invalid code. Please try again.", variant: "destructive" });
    } finally {
      setIsVerifying(false);
    }
  };

  const formatPhoneNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, "");
    const limited = cleaned.slice(0, 10);
    if (limited.length <= 3) return limited;
    if (limited.length <= 6) return `(${limited.slice(0, 3)})${limited.slice(3)}`;
    return `(${limited.slice(0, 3)})${limited.slice(3, 6)}-${limited.slice(6)}`;
  };

  const toE164Format = (formatted: string) => {
    const cleaned = formatted.replace(/\D/g, "");
    return cleaned.length === 10 ? `+1${cleaned}` : formatted;
  };

  const startSMSSetup = (reconfigure = false) => {
    // DEMO SANDBOX: gate BEFORE the setup panel opens.
    //
    // The backend blocks the SMS enrolment endpoints, so this was already refused — but
    // only after the visitor had opened the panel and typed a phone number, which reads as
    // a broken form. Binding a phone to a POOLED seat would also challenge the NEXT visitor
    // for a code only this one can receive, taking the seat out of the pool for good.
    if (isDemoMode()) {
      window.dispatchEvent(
        new CustomEvent('demo-restricted', {
          detail:
            "Setting up two-factor authentication is disabled in the demo sandbox. Start a free trial to secure your real account.",
        }),
      );
      return;
    }

    setShowSMSSetup(true);
    setSmsPhoneNumber("");
    setSmsVerificationCode("");
    setShowBackupCodes(false);
    setBackupCodesDownloaded(false);
    setIsReconfiguring(reconfigure);
  };

  const sendSMSCode = async () => {
    const cleaned = smsPhoneNumber.replace(/\D/g, "");
    if (!cleaned || cleaned.length !== 10) {
      toast({ title: "Invalid Phone", description: "Please enter a valid 10-digit phone number.", variant: "destructive" });
      return;
    }
    try {
      setIsSmsSending(true);
      const userId = await getUserId();
      await api.post({
        url: getApiUrl(endpoint.auth.setupMFA),
        body: { userId, method: "sms", phoneNumber: toE164Format(smsPhoneNumber) },
        auth: true,
      });
      toast({ title: "Code Sent", description: "Verification code sent to your phone." });
    } catch {
      toast({ title: "Error", description: "Failed to send verification code.", variant: "destructive" });
    } finally {
      setIsSmsSending(false);
    }
  };

  const verifySMSCode = async () => {
    if (!smsVerificationCode || smsVerificationCode.length !== 6) {
      toast({ title: "Invalid Code", description: "Please enter a 6-digit code.", variant: "destructive" });
      return;
    }
    if (!smsPhoneNumber) {
      toast({ title: "Error", description: "Phone number is missing.", variant: "destructive" });
      return;
    }
    try {
      setIsVerifying(true);
      const userId = await getUserId();
      const response = await api.post({
        url: getApiUrl(endpoint.auth.verifyMFA),
        body: { userId, code: smsVerificationCode, method: "sms", phoneNumber: toE164Format(smsPhoneNumber) },
        auth: true,
      });
      const responseData = (response.data as any)?.data || response.data;
      if (responseData?.backupCodes && responseData.backupCodes.length > 0) {
        setTotpSetupData({ manualEntryKey: "", qrCodeUrl: "", backupCodes: responseData.backupCodes } as TOTPSetupData);
        setShowBackupCodes(true);
        await fetchMFASettings();
      } else {
        toast({
          title: isReconfiguring ? "Phone Number Updated" : "SMS Enabled",
          description: isReconfiguring ? "Your phone number has been updated successfully." : "SMS authentication has been enabled successfully.",
        });
        setShowSMSSetup(false);
        await fetchMFASettings();
      }
    } catch (error: any) {
      toast({
        title: "Verification Failed",
        description: error?.response?.data?.message || "Invalid code. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const setPreferredMethod = async (method: "sms" | "totp") => {
    try {
      const userId = await getUserId();
      await api.post({
        url: getApiUrl(endpoint.auth.setPreferredMFA),
        body: { userId, preferredMethod: method },
        auth: true,
      });
      toast({
        title: "Preference Updated",
        description: `${method.toUpperCase()} is now your preferred authentication method.`,
      });
      await fetchMFASettings();
    } catch {
      toast({ title: "Error", description: "Failed to update preferred method.", variant: "destructive" });
    }
  };

  const downloadBackupCodes = () => {
    const codes = totpSetupData?.backupCodes || [];
    const blob = new Blob([codes.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${branding.brandName.toLowerCase().replace(/\s+/g, "-")}-backup-codes.txt`;
    a.click();
    URL.revokeObjectURL(url);
    setBackupCodesDownloaded(true);
  };

  // ── Loading skeleton ──
  if (isLoading) {
    return (
      <Card className="overflow-hidden">
        <div className="p-6 space-y-5">
          <div className="flex items-center gap-4">
            <div className="h-11 w-11 rounded-xl bg-muted animate-pulse" />
            <div className="space-y-2">
              <div className="h-5 w-52 bg-muted rounded animate-pulse" />
              <div className="h-3.5 w-64 bg-muted rounded animate-pulse" />
            </div>
          </div>
          <div className="h-px bg-border" />
          {[1, 2].map((i) => (
            <div key={i} className="h-[80px] rounded-xl bg-muted/50 animate-pulse" />
          ))}
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card className="overflow-hidden">
        {/* ── Header ── */}
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: EASE }}
          className="px-6 pt-6 pb-4"
        >
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center h-11 w-11 rounded-xl bg-primary text-primary-foreground shadow-sm">
              <Shield className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-xl font-semibold tracking-tight">Multi-Factor Authentication</h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                Add an extra layer of security to your account
              </p>
            </div>
          </div>
          <div className="mt-5 h-px bg-gradient-to-r from-primary/25 via-primary/10 to-transparent" />
        </motion.div>

        {/* ── Content ── */}
        <CardContent className="pt-0 pb-6">
          <motion.div variants={stagger} initial="hidden" animate="show" className="space-y-3">
            {/* Requirement notice */}
            <motion.div variants={fadeUp}>
              <div className="p-3 rounded-lg border-l-2 border-l-primary bg-primary/5">
                <p className="text-xs text-foreground/70 leading-relaxed">
                  <strong>Security Requirement:</strong>{" "}
                  {mfaSettings?.totpOnly
                    ? "An Authenticator App must be configured. It can't be removed, but you can reconfigure it."
                    : "Both SMS and Authenticator App must be configured. These methods cannot be removed, but you can reconfigure them."}{" "}
                  For issues, contact{" "}
                  <a href={`mailto:${branding.supportEmail}`} className="underline font-medium text-primary">
                    {branding.supportEmail}
                  </a>
                </p>
              </div>
            </motion.div>

            {/* Action required */}
            {!mfaSettings?.totpEnabled && (mfaSettings?.totpOnly || !mfaSettings?.smsEnabled) && (
              <motion.div variants={fadeUp}>
                <div className="p-3 rounded-lg border-l-2 border-l-destructive bg-destructive/5">
                  <p className="text-xs text-foreground/70 leading-relaxed">
                    <strong>Action Required:</strong> Set up {mfaSettings?.totpOnly ? "an Authenticator App" : "both SMS and Authenticator App"} to
                    secure your account and continue using the platform.
                  </p>
                </div>
              </motion.div>
            )}

            {/* Partial setup warning */}
            {!mfaSettings?.totpOnly && ((mfaSettings?.smsEnabled && !mfaSettings?.totpEnabled) ||
              (!mfaSettings?.smsEnabled && mfaSettings?.totpEnabled)) && (
              <motion.div variants={fadeUp}>
                <div className="p-3 rounded-lg border-l-2 border-l-amber-400 bg-amber-50/60">
                  <p className="text-xs text-foreground/70 leading-relaxed">
                    <strong>Almost there!</strong> Set up{" "}
                    {!mfaSettings?.smsEnabled ? "SMS Authentication" : "Authenticator App"} to
                    complete your security setup.
                  </p>
                </div>
              </motion.div>
            )}

            {/* ─── SMS Authentication (not offered to totpOnly accounts) ─── */}
            {!mfaSettings?.totpOnly && <motion.div variants={fadeUp}>
              <div
                className={cn(
                  "relative rounded-xl border transition-all duration-300",
                  "hover:shadow-md hover:shadow-black/[0.03]",
                  mfaSettings?.smsEnabled
                    ? "border-primary/20 bg-primary/[0.015]"
                    : "border-border"
                )}
              >
                <div
                  className={cn(
                    "absolute left-0 top-3 bottom-3 w-[3px] rounded-full transition-all duration-500",
                    mfaSettings?.smsEnabled ? "bg-emerald-500" : "bg-amber-400"
                  )}
                />

                <div className="flex items-center justify-between p-4 pl-5">
                  <div className="flex items-center gap-4">
                    <div
                      className={cn(
                        "flex items-center justify-center h-10 w-10 rounded-lg transition-colors duration-300",
                        mfaSettings?.smsEnabled
                          ? "bg-emerald-500/10 text-emerald-600"
                          : "bg-amber-100 text-amber-600"
                      )}
                    >
                      <Smartphone className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[15px] font-semibold leading-none">
                          SMS Authentication
                        </span>
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                          Required
                        </Badge>
                        {mfaSettings?.smsEnabled ? (
                          <Badge className="text-[10px] px-1.5 py-0 h-4 bg-emerald-600 hover:bg-emerald-600 border-0">
                            <CheckCircle className="h-2.5 w-2.5 mr-1" />
                            Configured
                          </Badge>
                        ) : (
                          <Badge variant="destructive" className="text-[10px] px-1.5 py-0 h-4">
                            <XCircle className="h-2.5 w-2.5 mr-1" />
                            Not Set Up
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1.5">
                        Receive verification codes via text message
                      </p>
                    </div>
                  </div>
                  <Button
                    variant={mfaSettings?.smsEnabled ? "outline" : "default"}
                    size="sm"
                    onClick={() => startSMSSetup(!!mfaSettings?.smsEnabled)}
                  >
                    {mfaSettings?.smsEnabled ? "Reconfigure" : "Set Up Now"}
                  </Button>
                </div>
              </div>
            </motion.div>}

            {/* ─── TOTP Authentication ─── */}
            <motion.div variants={fadeUp}>
              <div
                className={cn(
                  "relative rounded-xl border transition-all duration-300",
                  "hover:shadow-md hover:shadow-black/[0.03]",
                  mfaSettings?.totpEnabled
                    ? "border-primary/20 bg-primary/[0.015]"
                    : "border-border"
                )}
              >
                <div
                  className={cn(
                    "absolute left-0 top-3 bottom-3 w-[3px] rounded-full transition-all duration-500",
                    mfaSettings?.totpEnabled ? "bg-emerald-500" : "bg-amber-400"
                  )}
                />

                <div className="flex items-center justify-between p-4 pl-5">
                  <div className="flex items-center gap-4">
                    <div
                      className={cn(
                        "flex items-center justify-center h-10 w-10 rounded-lg transition-colors duration-300",
                        mfaSettings?.totpEnabled
                          ? "bg-emerald-500/10 text-emerald-600"
                          : "bg-amber-100 text-amber-600"
                      )}
                    >
                      <QrCode className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[15px] font-semibold leading-none">
                          Authenticator App
                        </span>
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                          Required
                        </Badge>
                        {mfaSettings?.totpEnabled ? (
                          <Badge className="text-[10px] px-1.5 py-0 h-4 bg-emerald-600 hover:bg-emerald-600 border-0">
                            <CheckCircle className="h-2.5 w-2.5 mr-1" />
                            Configured
                          </Badge>
                        ) : (
                          <Badge variant="destructive" className="text-[10px] px-1.5 py-0 h-4">
                            <XCircle className="h-2.5 w-2.5 mr-1" />
                            Not Set Up
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1.5">
                        Use an authenticator app — we recommend <strong>Google Authenticator</strong> (Authy or Apple Passwords also work)
                      </p>
                    </div>
                  </div>
                  <Button
                    variant={mfaSettings?.totpEnabled ? "outline" : "default"}
                    size="sm"
                    onClick={() => setupTOTP(!!mfaSettings?.totpEnabled)}
                  >
                    {mfaSettings?.totpEnabled ? "Reconfigure" : "Set Up Now"}
                  </Button>
                </div>
              </div>
            </motion.div>

            {/* Backup codes info */}
            {(mfaSettings?.smsEnabled || mfaSettings?.totpEnabled) && (
              <motion.div variants={fadeUp}>
                <div className="p-3 rounded-lg border-l-2 border-l-primary bg-primary/5">
                  <p className="text-xs text-foreground/70 leading-relaxed">
                    <strong>Backup Codes:</strong> Your backup codes were provided during initial
                    MFA setup. Store them safely — they are for emergency use only if you lose
                    access to your authentication methods.
                  </p>
                </div>
              </motion.div>
            )}

            {/* Support contact */}
            <motion.div variants={fadeUp}>
              <div className="text-center pt-2">
                <p className="text-xs text-muted-foreground">
                  Having trouble?{" "}
                  <a
                    href={`mailto:${branding.supportEmail}`}
                    className="text-primary hover:underline font-medium"
                  >
                    {branding.supportEmail}
                  </a>
                </p>
              </div>
            </motion.div>
          </motion.div>
        </CardContent>
      </Card>

      {/* ── TOTP Setup Dialog ── */}
      <Dialog
        open={showTOTPSetup}
        onOpenChange={(open) => {
          setShowTOTPSetup(open);
          if (!open) {
            setShowBackupCodes(false);
            setVerificationCode("");
            setIsReconfiguring(false);
          }
        }}
      >
        <ResponsiveDialogContent size="md">
          <DialogHeader>
            <DialogTitle>
              {isReconfiguring ? "Reconfigure Authenticator App" : "Setup Authenticator App"}
            </DialogTitle>
            <DialogDescription>
              {isReconfiguring
                ? "Scan the new QR code with your authenticator app. This will replace your existing configuration."
                : "Scan the QR code with your authenticator app and enter the verification code"}
            </DialogDescription>
          </DialogHeader>

          {isReconfiguring && (
            <Alert className="bg-yellow-50 border-yellow-300">
              <AlertCircle className="h-4 w-4 text-yellow-600" />
              <AlertDescription className="text-yellow-800">
                Reconfiguring will replace your existing authenticator setup. Make sure to scan the
                new QR code and save new backup codes.
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            {totpSetupData && (
              <>
                {!showBackupCodes ? (
                  <div className="bg-white rounded-lg border p-6 space-y-4">
                    <div className="flex justify-center">
                      <img
                        src={totpSetupData.qrCodeUrl}
                        alt="QR Code"
                        className="border rounded-lg w-48 h-48"
                      />
                    </div>
                    <div className="text-center space-y-2">
                      <p className="text-sm text-gray-600">Can&apos;t scan? Enter this code manually:</p>
                      <div className="bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">
                        <code className="text-sm font-mono font-semibold text-gray-900 break-all">
                          {totpSetupData.manualEntryKey}
                        </code>
                      </div>
                    </div>
                    <div className="space-y-2 pt-2">
                      <Label htmlFor="verificationCode">Enter 6-digit code</Label>
                      <Input
                        id="verificationCode"
                        placeholder="000000"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ""))}
                        maxLength={6}
                        className="text-center text-lg tracking-widest"
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="bg-green-50 border border-green-300 rounded-lg p-4 mb-4">
                      <h4 className="font-medium text-green-900 mb-2 flex items-center gap-2">
                        <CheckCircle className="h-4 w-4" />
                        Authenticator App Enabled!
                      </h4>
                      <p className="text-sm text-green-800">
                        Your authenticator app has been successfully enabled.
                      </p>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
                      <h4 className="font-medium text-yellow-900 mb-2 flex items-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        Save Your Backup Codes
                      </h4>
                      <p className="text-sm text-yellow-800 mb-3">
                        Download these codes and store them safely. You&apos;ll need them if you lose
                        access to your authenticator app. Each code can only be used once.
                      </p>
                      <div className="grid grid-cols-2 gap-2 mb-3 max-h-48 overflow-y-auto">
                        {totpSetupData.backupCodes.map((code, index) => (
                          <div
                            key={index}
                            className="bg-white p-2 rounded border text-center font-mono text-sm"
                          >
                            {code}
                          </div>
                        ))}
                      </div>
                      <Button
                        onClick={downloadBackupCodes}
                        variant="outline"
                        size="sm"
                        className="w-full"
                        disabled={backupCodesDownloaded}
                      >
                        {backupCodesDownloaded ? (
                          <>
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Downloaded
                          </>
                        ) : (
                          <>
                            <Download className="h-4 w-4 mr-2" />
                            Download Backup Codes
                          </>
                        )}
                      </Button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          <DialogFooter>
            {!showBackupCodes ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowTOTPSetup(false);
                    setVerificationCode("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={verifyTOTP}
                  disabled={verificationCode.length !== 6 || isVerifying}
                >
                  {isVerifying ? "Verifying..." : "Verify & Enable"}
                </Button>
              </>
            ) : (
              <Button
                onClick={async () => {
                  setShowTOTPSetup(false);
                  setShowBackupCodes(false);
                  setVerificationCode("");
                  await fetchMFASettings();
                }}
                disabled={!backupCodesDownloaded}
                className="w-full"
              >
                {backupCodesDownloaded ? "Done" : "Please Download Backup Codes First"}
              </Button>
            )}
          </DialogFooter>
        </ResponsiveDialogContent>
      </Dialog>

      {/* ── SMS Setup Dialog ── */}
      <Dialog
        open={showSMSSetup}
        onOpenChange={(open) => {
          setShowSMSSetup(open);
          if (!open) {
            setSmsPhoneNumber("");
            setSmsVerificationCode("");
            setIsReconfiguring(false);
          }
        }}
      >
        <ResponsiveDialogContent size="md">
          <DialogHeader>
            <DialogTitle>
              {isReconfiguring ? "Change Phone Number" : "Setup SMS Authentication"}
            </DialogTitle>
            <DialogDescription>
              {isReconfiguring
                ? "Enter a new phone number to update your SMS authentication."
                : "Enter your phone number to receive verification codes via SMS."}
            </DialogDescription>
          </DialogHeader>

          {isReconfiguring && (
            <Alert className="bg-yellow-50 border-yellow-300">
              <AlertCircle className="h-4 w-4 text-yellow-600" />
              <AlertDescription className="text-yellow-800">
                Changing your phone number will update where SMS codes are sent. Make sure you have
                access to the new number.
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-4 py-4">
            {!showBackupCodes ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    placeholder="(555)555-5555"
                    value={smsPhoneNumber}
                    onChange={(e) => setSmsPhoneNumber(formatPhoneNumber(e.target.value))}
                    disabled={isSmsSending || smsVerificationCode.length > 0}
                    maxLength={13}
                  />
                  <p className="text-xs text-muted-foreground">US phone number only</p>
                </div>
                {smsVerificationCode.length === 0 && (
                  <Button
                    onClick={sendSMSCode}
                    disabled={isSmsSending || smsPhoneNumber.replace(/\D/g, "").length !== 10}
                    className="w-full"
                  >
                    {isSmsSending ? "Sending..." : "Send Verification Code"}
                  </Button>
                )}
                {smsVerificationCode.length > 0 ||
                (smsPhoneNumber.length >= 10 && !isSmsSending) ? (
                  <div className="space-y-2">
                    <Label htmlFor="sms-code">Verification Code</Label>
                    <Input
                      id="sms-code"
                      placeholder="Enter 6-digit code"
                      value={smsVerificationCode}
                      onChange={(e) =>
                        setSmsVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                      }
                      maxLength={6}
                    />
                    <p className="text-xs text-muted-foreground">
                      Enter the 6-digit code sent to your phone
                    </p>
                  </div>
                ) : null}
              </>
            ) : (
              <div className="space-y-4">
                <Alert className="bg-green-50 border-green-300">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    SMS authentication has been enabled successfully! Please save your backup codes
                    below.
                  </AlertDescription>
                </Alert>
                <div className="bg-gray-50 p-6 rounded-lg border space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-sm">Backup Codes</h4>
                    <Button variant="outline" size="sm" onClick={downloadBackupCodes} className="h-8">
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Save these codes in a secure place. Each code can only be used once.
                  </p>
                  <div className="grid grid-cols-2 gap-3 font-mono text-sm">
                    {totpSetupData?.backupCodes?.map((code, idx) => (
                      <div key={idx} className="bg-white px-3 py-2 rounded border text-center">
                        {code}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="sm:justify-between">
            {!showBackupCodes ? (
              <>
                <Button type="button" variant="outline" onClick={() => setShowSMSSetup(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={verifySMSCode}
                  disabled={smsVerificationCode.length !== 6 || isVerifying}
                >
                  {isVerifying ? "Verifying..." : "Verify & Enable"}
                </Button>
              </>
            ) : (
              <Button
                onClick={async () => {
                  setShowSMSSetup(false);
                  setShowBackupCodes(false);
                  await fetchMFASettings();
                }}
                disabled={!backupCodesDownloaded}
                className="w-full"
              >
                {backupCodesDownloaded ? "Done" : "Please Download Backup Codes First"}
              </Button>
            )}
          </DialogFooter>
        </ResponsiveDialogContent>
      </Dialog>
    </>
  );
};
